import { FC, useState } from 'react';
import { Modal, Radio, Spin } from 'antd';
import { useMethods } from '@lhb/hook';
import IconFont from '@/common/components/Base/IconFont';
import FormSetName from '@/common/components/Form/FormSetName/FormSetName';
import V2Empty from '@/common/components/Data/V2Empty';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { getSpotSurveyDetail, getSpotSurveySimplePage } from '@/common/api/expandStore/spotSurvey';
import { formDataEcho, parseValueCatch, setComValue } from '../../config';
import { addressIdentification } from '../../customize';
import { getCollisionCoordinates } from '../AreaProtection/collision';
import { synchronizeSpotSurveyValues } from './utils';
import styles from './index.module.less';

interface AssociatedRecord {
  id: number;
  address?: string;
  createdAt?: string;
  employeeName?: string;
  distance?: number;
  [key: string]: any;
}

/**
 * @description 机会点动态表单项-关联踩点记录
 */
const AssociatedRecords: FC<{
  propertyItem: any;
  form: any;
  disabled?: boolean;
  addressVal?: any;
  tiledFormDataRef: any;
  updateRelateShow?: any;
}> = ({
  propertyItem,
  form,
  disabled,
  addressVal,
  tiledFormDataRef,
  updateRelateShow,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [synchronizing, setSynchronizing] = useState(false);
  const [records, setRecords] = useState<AssociatedRecord[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const currentRecord: AssociatedRecord | null = form.getFieldValue(propertyItem.identification) || formDataEcho(propertyItem);
  const label = propertyItem.anotherName || propertyItem.name || '关联踩点记录';

  const methods = useMethods({
    getCoordinates() {
      const addressComponent = tiledFormDataRef?.current?.find((item: any) => item.identification === addressIdentification);
      const addressValue = parseValueCatch(addressComponent);
      return getCollisionCoordinates(addressValue, addressVal);
    },
    closeModal() {
      setModalOpen(false);
    },
    handleRecordChange(event) {
      setSelectedId(event.target.value);
    },
    openModal() {
      const coordinates = methods.getCoordinates();
      if (!coordinates) {
        V2Message.warning('请选择详细地址后再进行操作');
        return;
      }
      const [lng, lat] = coordinates;
      setModalOpen(true);
      setFetching(true);
      getSpotSurveySimplePage({ longitude: lng, latitude: lat, distance: 500 }).then((data) => {
        const records = Array.isArray(data?.objectList) ? data.objectList : [];
        const currentId: number | null = typeof currentRecord?.id === 'number' ? currentRecord.id : null;
        setRecords(records);
        setSelectedId(records.some((item: AssociatedRecord) => item.id === currentId) ? currentId : null);
      }, (error) => {
        console.log('关联踩点记录查询失败', error);
        setRecords([]);
        setSelectedId(null);
      }).finally(() => {
        setFetching(false);
      });
    },
    confirmSelection() {
      const selectedRecord = records.find((item) => item.id === selectedId);
      if (!selectedRecord) {
        V2Message.warning('请选择踩点记录');
        return;
      }
      setSynchronizing(true);
      getSpotSurveyDetail({ id: selectedRecord.id }).then((detail) => {
        setComValue(propertyItem, selectedRecord);
        const { importedComponents, importedIdentifications } = synchronizeSpotSurveyValues(detail?.propertyGroupVOList, tiledFormDataRef?.current || []);
        const formValues: any = {
          [propertyItem.identification]: formDataEcho(propertyItem),
        };
        importedComponents.forEach((component: any) => {
          formValues[component.identification] = formDataEcho(component);
          updateRelateShow?.(component);
        });
        form.setFieldsValue(formValues);
        updateRelateShow?.(propertyItem);
        setModalOpen(false);
        V2Message.success(`已从「${selectedRecord.address || '踩点记录'}」同步并覆盖 ${importedIdentifications.length} 个字段。`);
      }, () => {
        V2Message.error('踩点记录同步失败，请重试');
      }).finally(() => {
        setSynchronizing(false);
      });
    },
  });

  return <>
    <FormSetName name={propertyItem.identification} initialValue={null} />
    <div className={styles.associatedRecords}>
      {currentRecord?.id ? <div className={styles.selectedRecord}>
        <div className={styles.recordMain}>
          <div className={styles.recordTitle}>
            <div className={styles.recordTitleName}>{currentRecord.address}</div>
            <em>{currentRecord.distance}m</em>
          </div>
          <div className={styles.recordMeta}>
            <div>踩点时间：{currentRecord.createdAt}</div>
            <div className={styles.recordMetaDivider}></div>
            <div>踩点人：{currentRecord.employeeName}</div>
          </div>
        </div>
        {!disabled ? <div className={styles.selectedRecordBtn} onClick={methods.openModal}>重新选择</div> : null}
      </div> : <div className={`${styles.emptyRecord} ${disabled ? '' : styles.emptyRecordClickable}`} onClick={disabled ? undefined : methods.openModal}>
        <IconFont iconHref='iconic_add_xiangqing' className={styles.emptyIcon} />
        <div className={styles.emptyContent}>
          <div className={styles.emptyContentTitle}>{label}</div>
          <p className={styles.emptyContentTip}>尚未关联踩点记录，点击选择记录</p>
        </div>
      </div>}
    </div>
    <Modal
      title='选择踩点记录'
      open={modalOpen}
      width={468}
      okText='关联并同步'
      cancelText='取消'
      okButtonProps={{ disabled: !selectedId || synchronizing }}
      onCancel={methods.closeModal}
      onOk={methods.confirmSelection}
      destroyOnClose
    >
      <div className={styles.modalTip}>按距离由近到远排序．单选整条关联并同步</div>
      <Spin spinning={fetching || synchronizing}>
        {!fetching && !records.length ? <V2Empty customTip='暂无踩点记录' /> : <Radio.Group className={styles.recordList} value={selectedId} onChange={methods.handleRecordChange}>
          {records.map((record) => <Radio key={record.id} value={record.id} className={styles.recordOption}>
            <div className={styles.recordTitle}>
              <div className={styles.recordTitleLeft}>{record.address}</div>
              <div className={styles.recordTitleRight}>{record.distance}m</div>
            </div>
            <div className={styles.recordMeta}>
              <div>踩点时间：{record.createdAt}</div>
              <div className={styles.recordMetaDivider}></div>
              <div>踩点人：{record.employeeName}</div>
            </div>
          </Radio>)}
        </Radio.Group>}
      </Spin>
    </Modal>
  </>;
};

export default AssociatedRecords;
